import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { UpsellCard } from '../components/UpsellCard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Experiences = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [upsells, setUpsells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');

  useEffect(() => {
    const fetchUpsells = async () => {
      try {
        const response = await axios.get(`${API}/upsells`);
        setUpsells(response.data);
      } catch (error) {
        console.error('Error fetching upsells:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUpsells();
  }, []);

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'spa', label: t('experiences.spa') },
    { id: 'shuttle', label: t('experiences.shuttle') },
    { id: 'excursion', label: t('experiences.excursion') },
  ];

  const filteredUpsells =
    activeCategory === 'all' ? upsells : upsells.filter((u) => u.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="experiences-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1561718541-b00339c8db2a?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Experiences"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0F1F3C]/50" />
        </div>
        <div className="relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-sans font-medium mb-4">
              Curated For You
            </p>
            <h1 className="font-serif text-5xl lg:text-6xl text-white">{t('experiences.title')}</h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-3 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300 ${
                  activeCategory === cat.id
                    ? 'bg-[#0F1F3C] text-white'
                    : 'bg-white text-[#0F1F3C] hover:bg-[#0F1F3C]/5'
                }`}
                data-testid={`category-filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Upsells Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 h-48 mb-4" />
                  <div className="bg-gray-200 h-6 w-2/3 mb-2" />
                  <div className="bg-gray-200 h-4 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredUpsells.map((upsell, index) => (
                <UpsellCard
                  key={upsell.id}
                  upsell={upsell}
                  index={index}
                  isSelected={false}
                  onToggle={() => {}}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
